import * as React from "react";
import { useEffect, useRef, useState, useMemo } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { getMinecraftData, getMinecraftPageData } from "../api/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText
} from "@mui/material";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from "react-i18next";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { getContent } from "../utils";

export const minecraftLoader = async () => {
  try {
    const response = await getMinecraftData();

    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const minecraftPageLoader = async (key) => {
  try {
    const response = await getMinecraftPageData(key);

    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};


export const Minecraft = () => {
  const [content, setContent] = useState(null);
  const data = useLoaderData();

  const navigate = useNavigate();
  const location = useLocation();

  const hashId = useMemo(() => {
    const h = location.hash || "";
    return h.startsWith("#") ? h.slice(1) : h;
  }, [location.hash]);

  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const contentRef = useRef(null);
  const getGroupKey = (group) => group.id;

  const minecraftNavGroups = useMemo(() => content || [], [content]);

  const minecraftNavItems = minecraftNavGroups.flatMap((group) => group.items);
  const initialSection = minecraftNavGroups?.[0]?.items?.[0]?.id;
  const [activeSection, setActiveSection] = useState(initialSection);

  useEffect(() => {
    if (!minecraftNavItems?.length) return;

    const exists =
      hashId && minecraftNavItems.some((it) => String(it.id) === String(hashId));

    const next = exists
      ? hashId
      : (minecraftNavGroups?.[0]?.items?.[0]?.id ?? null);

    if (next && String(next) !== String(activeSection)) {
      setActiveSection(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minecraftNavItems, hashId]);

  const [pageContent, setPageContent] = useState(null);

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initialExpanded = {};
    minecraftNavGroups.forEach((group) => {
      initialExpanded[getGroupKey(group)] = true;
    });
    return initialExpanded;
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setContent(data);
  }, [data]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Єдине Україномовне Ком'юніті - ЄУК Майнкрафт",
      "url": "https://uuc.com.ua/minecraft",
      "description": "ЄУК, українські майнкрафт сервери, український майнкрафт, майнкрафт сервери, майнкрафт ком'юніті, майнкрафт спільнота, майнкрафт сервер, українська спільнота"
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!minecraftNavGroups) return;
    const initialExpanded = {};
    minecraftNavGroups.forEach((group) => {
      initialExpanded[getGroupKey(group)] = true;
    });
    setExpandedGroups(initialExpanded);
  }, [minecraftNavGroups, activeSection]);

  const fetchPageContent = async (key) => {
    const response = await minecraftPageLoader(key);
    setPageContent(response);
  }

  useEffect(() => {
    if (activeSection) {
      fetchPageContent(activeSection)
    }
  }, [activeSection])

  const handleSectionSelect = (id) => {
    setActiveSection(id);
    setMobileNavOpen(false);

    // Write hash into URL (keeps same pathname, supports back/forward)
    navigate({ hash: `#${id}` }, { replace: false });

    requestAnimationFrame(() => {
      const anchor = document.querySelector("#back-to-top-anchor");
      if (anchor) {
        anchor.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    });
  };

  const activeNavIndex = minecraftNavItems.findIndex(
    (item) => item.id === activeSection
  );
  const activeNavItem = activeNavIndex >= 0 ? minecraftNavItems[activeNavIndex] : null;
  const prevItem = activeNavIndex > 0 ? minecraftNavItems[activeNavIndex - 1] : null;
  const nextItem = activeNavIndex >= 0 && activeNavIndex < minecraftNavItems.length - 1
    ? minecraftNavItems[activeNavIndex + 1]
    : null;

  const getNavLabel = (item) => getContent(item, "name", language);

  const MinecraftSection = ({ sectionContent }) => {
    const title = sectionContent ? getContent(sectionContent, "name", language) : "";
    const resolvedTitle = title || (activeNavItem ? getNavLabel(activeNavItem) : "");
    const htmlContent = sectionContent
      ? (getContent(sectionContent, "content", language)
        || getContent(sectionContent, "text", language)
        || "")
      : "";
    const prevLabel = prevItem
      ? `${t("minecraft.nav.previous")}: ${getNavLabel(prevItem)}`
      : t("minecraft.nav.previous");
    const nextLabel = nextItem
      ? `${t("minecraft.nav.next")}: ${getNavLabel(nextItem)}`
      : t("minecraft.nav.next");

    return (
      <Container
        maxWidth="none"
        sx={{
          padding: { xs: "6em 2em 3em 2em !important", md: "4em 4em 4em 4em !important" },
          width: "100%",
          maxWidth: "1200px",
          height: '100%',
          flex: 1,
          color: "white"
        }}
      >
        {resolvedTitle ? (
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: 26, md: 40 },
              fontWeight: 700,
              mb: 3
            }}
          >
            {resolvedTitle}
          </Typography>
        ) : null}
        {resolvedTitle ? (
          <Box
            sx={{
              borderTop: "1px solid rgba(255, 255, 255, 0.18)",
              mb: 3
            }}
          />
        ) : null}
        <Box
          sx={{
            fontWeight: 400,
            letterSpacing: 0.4,
            fontSize: { xs: 14, md: 16 },
            lineHeight: 1.7,
            fontFamily: "Montserrat",
            "& > p": {
              marginBlockStart: 2.5,
              marginBlockEnd: 2.5
            },
            "& a": {
              color: "#34C542"
            },
            "& img": {
              maxWidth: "100%",
              width: { xs: "100%", sm: "auto" },
              height: "auto !important",
              display: "block",
            }
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        <Box
          sx={{
            borderTop: "1px solid rgba(255, 255, 255, 0.18)",
            mt: 4,
            mb: 3
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            mt: 4,
            flexWrap: "wrap"
          }}
        >
          <Button
            variant="outlined"
            disabled={!prevItem}
            onClick={() => prevItem && handleSectionSelect(prevItem.id)}
            sx={{
              borderColor: "rgba(123, 207, 134, 0.7)",
              color: "rgba(123, 207, 134, 0.9)",
              textTransform: "none",
              fontSize: { xs: 12, md: 13 },
              "&:hover": {
                borderColor: "rgba(104, 187, 115, 0.9)",
                backgroundColor: "rgba(123, 207, 134, 0.08)"
              }
            }}
          >
            {prevLabel}
          </Button>
          <Button
            variant="outlined"
            disabled={!nextItem}
            onClick={() => nextItem && handleSectionSelect(nextItem.id)}
            sx={{
              borderColor: "rgba(123, 207, 134, 0.7)",
              color: "rgba(123, 207, 134, 0.9)",
              textTransform: "none",
              fontSize: { xs: 12, md: 13 },
              "&:hover": {
                borderColor: "rgba(104, 187, 115, 0.9)",
                backgroundColor: "rgba(123, 207, 134, 0.08)"
              }
            }}
          >
            {nextLabel}
          </Button>
        </Box>
      </Container>
    );
  };

  const handleGroupToggle = (label) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const renderNav = (isMobile = false) => (
    <Box
      sx={{
        width: isMobile ? '100%' : 260,
        p: 2,
        backgroundColor: "#000000",
        paddingTop: { xs: "2em", md: "6.5em", lg: "8.5em" },
        borderRight: "1px solid #242424",
        height: '100%',
        position: 'relative',
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        overscrollBehavior: "contain",
      }}
    >
      {minecraftNavGroups.map((group) => {
        const groupKey = getGroupKey(group);
        return (
          <List key={groupKey} disablePadding sx={{ mb: 1.5 }}>
            <ListItemButton
              onClick={() => handleGroupToggle(groupKey)}
              sx={{
                borderRadius: 2,
                px: 1.5,
                py: 1,
                color: "#A0A0A0",
                "&:hover": {
                  backgroundColor: "#1B1B1B"
                }
              }}
            >
              <ListItemText
                primary={getNavLabel(group)}
                primaryTypographyProps={{
                  fontSize: 11,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  fontWeight: 600
                }}
              />
              {expandedGroups[groupKey] ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </ListItemButton>
            <Collapse in={expandedGroups[groupKey]} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ mt: 0.5 }}>
                {group.items.map((item) => {
                  const isSelected = activeSection === item.id;
                  return (
                    <ListItemButton
                      key={item.id}
                      onClick={() => handleSectionSelect(item.id)}
                      selected={isSelected}
                      sx={{
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        ml: 0.5,
                        color: "white",
                        "&.Mui-selected": {
                          backgroundColor: "#34C542",
                          color: "#0B0B0B",
                          "& .MuiListItemText-primary": {
                            fontWeight: 700
                          }
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#2FB43D"
                        }
                      }}
                    >
                      <ListItemText
                        primary={getNavLabel(item)}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: isSelected ? 700 : 500
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </List>
        );
      })}
    </Box>
  );

  return (
    <>
      {!content ? <Loading /> : null}
      <Helmet>
        <title>Єдине Україномовне Ком'юніті - ЄУК Майнкрафт</title>
        <meta name="description"
              content="«Єдине Україномовне Ком'юніті» – спільнота, що просуває українську мову та україномовний контент в інтернеті" />
        <meta name="keywords"
              content="ЄУК, українські майнкрафт сервери, український майнкрафт, майнкрафт сервери, майнкрафт ком'юніті, майнкрафт спільнота, майнкрафт сервер, українська спільнота" />
        <meta property="og:title" content="Єдине Україномовне Ком'юніті - ЄУК Майнкрафт" />
        <meta property="og:url" content="https://uuc.com.ua/minecraft" />
        <meta property="og:description"
              content="«Єдине Україномовне Ком'юніті» – спільнота, що просуває українську мову та україномовний контент в інтернеті" />
      </Helmet>
      <Header />
      <Container
        maxWidth="none"
        sx={{
          flexGrow: 1,
          flex: 1,
          padding: { xs: "0 0 1em 0 !important", md: "2em 0 0 0 !important" },
          display: 'flex',
          width: "100vw",
          mt: { xs: "73px", md: 0 }
        }}
      >
        <Box
          component="aside"
          sx={{
            position: 'fixed',
            overflow: "hidden",
            top: 0,
            height: "100vh",
            display: { xs: "none", md: "block" },
            alignSelf: "flex-start",
            zIndex: 2,
          }}
        >
          {renderNav()}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            flex: 1,
            width: "100%",
            overflow: "hidden",
            backgroundColor: "#101010",
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              position: 'fixed',
              top: 100,
              left: -5,
            }}
          >
            <IconButton
              onClick={() => setMobileNavOpen(true)}
              sx={{
                color: "white",
                backgroundColor: "#1B1B1B",
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: "#242424"
                }
              }}
            >
              <KeyboardDoubleArrowRightIcon fontSize="large" />
            </IconButton>
          </Box>
          <Box
            ref={contentRef}
            sx={{
              display: 'flex',
              flex: 1,
              padding: { md: "4em 0 0 0 !important" },
              marginLeft: { xs: 0, md: '290px' },
            }}
          >
            <MinecraftSection sectionContent={pageContent} />
          </Box>
        </Box>
      </Container>
      <Drawer
        anchor="left"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        PaperProps={{
          sx: {
            overflow: 'hidden',
            width: 310,
            borderRight: "1px solid #242424",
          }
        }}
      >
        {renderNav(true)}
      </Drawer>
      <Footer sx={{ marginLeft: { xs: 0, md: '290px' }, width: { xs: '100%', md: "calc(100vw - 290px)" } }} />
    </>
  );
};
